import React, { useContext, useEffect } from 'react';
import { To } from 'react-router-dom';
import {
	capitalizeFirstLetter,
	getVignetteIntroCollection,
	ITypeVignetteIntroFields,
	OfferContext,
	VignetteContext,
} from '@adac-mp/shared';
import { useQuery } from '@apollo/client';
import { useViewport } from '../../hooks/useViewport';
import { Typography, Wrapper, ArrowRight, Button, TrackingLink, ContentLoader, Richtext, Contact } from '../../index';
import { withTracking } from '../../utils/tracking/tracking';

type Country = 'österreich' | 'tschechien' | 'slowenien' | 'schweiz';

interface IWalkthroughProps {
	title: string;
	text: string;
	linkUrl: To;
	image: string;
	slug: string;
	country: Country;
}

const WalkthroughComponent = ({ title, text, linkUrl, image, country, slug }: IWalkthroughProps) => {
	const { contentfulLabels, updateContentfulLabels } = useContext(VignetteContext);
	const currentLabels = contentfulLabels as ITypeVignetteIntroFields;
	const { labels, lang } = useContext(OfferContext);
	const { loading, error, data } = useQuery(getVignetteIntroCollection, {
		variables: { lang: lang || 'de', id: slug },
	});

	const { viewport } = useViewport();
	const isDesktop = viewport === 'desktop';

	useEffect(() => {
		if (!error) {
			updateContentfulLabels(data?.vignetteIntroCollection.items[0]);
		}
	}, [data]);

	const renderVignetteLink = () => {
		const buttonText = currentLabels?.infoLinkText || `Weitere Infos zur Maut in ${capitalizeFirstLetter(country)}`;
		const setVignetteLinkPath = () => {
			if (country === 'österreich') {
				return currentLabels?.infoLinkUrl || 'https://www.adac.de/fahrzeugwelt/maut-vignette/oesterreich/';
			}
			return currentLabels?.infoLinkUrl || `https://www.adac.de/fahrzeugwelt/maut-vignette/${country}`;
		};
		return (
			<Button
				href={setVignetteLinkPath()}
				variant="tertiary"
				target="_blank"
				className="py-2 md:w-fit md:mt-4.5 gap-2"
				tracking={{
					info_click: {
						label: buttonText,
						element: 'Onboarding',
					},
				}}>
				<ArrowRight fill="currentColor" size="20" />
				{buttonText}
			</Button>
		);
	};

	const renderFooter = () => (
		<>
			<div className="flex flex-col gap-1.5 bg-white sticky bottom-0 left-0 right-0 p-1.5 border-solid border-l-0 border-b-0 border-r-0 border-t border-t-gray-600 md:static md:w-2/12 md:border-t-0 md:p-0">
				<TrackingLink
					className="inline-flex items-center justify-center rounded text-gray-100 no-underline font-bold relative transition ease-in-out duration-300 bg-primary text-lg p-2 disabled:bg-gray-600 disabled:text-gray-400 border-solid border-2 border-transparent hover:border-gray-100 focus-visible:border-gray-100  active:border-gray-100 before:content-['*'] before:absolute before:bg-black before:w-full before:h-full before:opacity-0 before:border before:transition before:ease-in-out before:duration-300 before:hover:opacity-5 before:focus-visible:opacity-5  before:active:opacity-5 focus-visible:outline-black focus-visible:outline focus-visible:outline-[3px] outline-offset-4 focus-visible:outline-black"
					to={linkUrl}
					tracking={{
						funnel_click: {
							label: labels.find(({ key }) => key === 'mp.onboarding.nextButton')?.value || "Los geht's",
							element: 'Onboarding',
						},
					}}>
					{labels.find(({ key }) => key === 'mp.onboarding.nextButton')?.value || "Los geht's"}
				</TrackingLink>
				{!isDesktop && renderVignetteLink()}
			</div>
			{isDesktop && renderVignetteLink()}
		</>
	);

	return (
		<>
			{loading ? (
				<ContentLoader isLoading hasPortal={false} />
			) : (
				<>
					<Wrapper className="bg-white" innerClass="items-center">
						<div className="text-center px-4 md:w-1/2">
							<Typography className="mb-3" component="h1" size="h2" variant="header" color="title">
								{currentLabels?.headline || title}
							</Typography>
							<Typography component="div" size="lg" color="title">
								{currentLabels?.text ? <Richtext document={currentLabels?.text} /> : text}
							</Typography>
						</div>
						<div className="flex justify-center mt-6 md:mb-6">
							<img src={image} alt={`${capitalizeFirstLetter(country)} Vignette Start Icon`} />
						</div>
						{isDesktop && renderFooter()}
					</Wrapper>
					{!isDesktop && renderFooter()}
					<Contact withSeparator />
				</>
			)}
		</>
	);
};

export const Walkthrough = withTracking(WalkthroughComponent);
